/**
 * Runtime-config voor de API-basis-URL: één omgevingsonafhankelijk release-artefact dat
 * per omgeving door de deploy-pipeline geconfigureerd wordt (zelfde model als
 * `src/Gedenk.EditorWeb`).
 *
 * `public/env.js` (na build: `dist/env.js`) zet `window.__ENV` en wordt in `index.html`
 * met een gewone (niet-module) script-tag geladen VÓÓR de app-bundle. Klassieke scripts
 * draaien tijdens het parsen, module-scripts pas daarna — `window.__ENV` staat dus klaar
 * voordat enige module evalueert, ook voor `API_BASE_URL` in `src/api/client.ts`, die bij
 * module-load wordt bepaald.
 *
 * Offline is dat script er niet: de service worker precachet `env.js` bewust niet (zie
 * `vite.config.ts`), dus buiten bereik van het netwerk faalt de tag stil, blijft
 * `window.__ENV` ongedefinieerd en geldt de terugval hieronder. De app breekt daar niet
 * op: de rit staat in IndexedDB en de wachtrij blijft gewoon staan.
 */

/** Het API-pad achter de basis-URL. Contract §1.1: alles hangt onder `/api/v1`. */
const API_PATH = '/api/v1';

/**
 * Bepaalt de basis-URL waar de HTTP-client zijn paden achter plakt (dus **inclusief**
 * `/api/v1`, want zo is `VITE_API_BASE_URL` in dit project altijd bedoeld).
 *
 * Precedentie:
 *  1. Een niet-lege `window.__ENV.API_URL` — de runtime-config van de deploy-pipeline wint
 *     altijd van build-time waarden. Die waarde is de basis-URL **zonder** `/api/v1`
 *     (hetzelfde contract als EditorWeb), dus het pad wordt hier toegevoegd; een trailing
 *     slash wordt genormaliseerd zodat er nooit `//api/v1` ontstaat.
 *  2. Anders de build-time `VITE_API_BASE_URL` — die is **inclusief** `/api/v1` en houdt
 *     die betekenis; zo blijven `.env`, `.env.local` en `npm run dev:real` werken.
 *  3. Anders `/api/v1` — same-origin, het gedrag van de dev-proxy en van MSW.
 *
 * Een lege of alleen-witruimte waarde telt in beide gevallen als "niet geconfigureerd":
 * de gecommitte default (`API_URL: ''`) mag dev en test niet van gedrag doen veranderen.
 * Pure functie, zonder globals — daarom apart van `getApiBaseUrl()` testbaar.
 */
export function resolveApiBaseUrl(
  runtimeApiUrl: unknown,
  buildTimeBaseUrl: string | undefined,
): string {
  if (typeof runtimeApiUrl === 'string') {
    const origin = runtimeApiUrl.trim().replace(/\/+$/, '');
    if (origin !== '') return `${origin}${API_PATH}`;
  }
  const buildTime = (buildTimeBaseUrl ?? '').trim().replace(/\/+$/, '');
  if (buildTime !== '') return buildTime;
  return API_PATH;
}

/**
 * De basis-URL van `Gedenk.Api` voor deze omgeving, inclusief `/api/v1` en zonder
 * trailing slash. Veilig zonder `window` (node/SSR-testomgevingen).
 */
export function getApiBaseUrl(): string {
  const runtime = typeof window !== 'undefined' ? window.__ENV?.API_URL : undefined;
  return resolveApiBaseUrl(runtime, import.meta.env.VITE_API_BASE_URL);
}
